import { useEffect, useState, useCallback } from "react";
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, Car, Search, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur2, fdate } from "@/lib/format";

const NONE = "__none__";

export function DriverPicker({ employees, value, onChange, testid = "cv-driver-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = employees.find((e) => e.id === value);
  const filtered = employees.filter((e) => `${e.first_name} ${e.last_name} ${e.rh_id || ""} ${e.current_vehicle || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 text-sm">
        <span className={sel ? "text-[#18221F]" : "text-[#8695AB]"}>{sel ? `${sel.first_name} ${sel.last_name}` : "Désigner un conducteur…"}</span>
        <Search className="h-3.5 w-3.5 text-[#8695AB]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#E2E8F0] bg-white shadow-lg">
            <div className="border-b border-[#EDF0F5] p-2"><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, prénom, matricule…" className="h-8" data-testid="cv-driver-search" /></div>
            <div className="max-h-56 overflow-y-auto py-1">
              {value && <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="block w-full px-3 py-1.5 text-left text-xs text-[#8695AB] hover:bg-[#F5F7FA]">— Retirer le conducteur —</button>}
              {filtered.length === 0 ? <p className="px-3 py-3 text-center text-xs text-[#8695AB]">Aucun conducteur</p> : filtered.map((e) => (
                <button key={e.id} type="button" onClick={() => { onChange(e.id); setOpen(false); }} className="block w-full px-3 py-1.5 text-left hover:bg-[#F5F7FA]" data-testid="cv-driver-option">
                  <span className="text-sm font-medium text-[#18221F]">{e.first_name} {e.last_name}</span>
                  <span className="ml-2 text-xs text-[#8695AB]">{e.rh_id ? `#${e.rh_id}` : ""} {e.current_vehicle ? `· ${e.current_vehicle}` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Chip = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{label} {ok ? "✓" : "⚠"}
  </span>
);

function ImportCard({ item, vehicles, employees, onValidated, onRemove }) {
  const [it, setIt] = useState(item);
  const [saving, setSaving] = useState(false);
  const [force, setForce] = useState(false);
  const set = (k, v) => setIt((s) => ({ ...s, [k]: v }));

  const token = localStorage.getItem("odc_token");
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${it.document_id}/download?auth=${token}`;
  const veh = vehicles.find((v) => v.id === it.vehicle_id);

  const validate = async () => {
    setSaving(true);
    try {
      const payload = {
        category: it.category, is_fps: it.is_fps,
        infraction_type: it.infraction_type, notice_number: it.notice_number,
        infraction_date: it.infraction_date, infraction_time: it.infraction_time,
        location: it.location,
        initial_amount: it.amount === "" || it.amount == null ? null : Number(it.amount),
        points: it.points === "" || it.points == null ? null : Number(it.points),
        payment_deadline: it.payment_deadline,
        vehicle_id: it.vehicle_id || "", employee_id: it.employee_id || "",
        registration: veh ? veh.registration : it.plate,
        status: it.employee_id ? "conducteur_designe" : (it.vehicle_id ? "a_traiter" : "conducteur_a_designer"),
        attach_document_id: it.document_id,
      };
      await api.post("/contraventions", payload);
      toast.success("Contravention enregistrée");
      onValidated(it.document_id);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="cv-import-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 shrink-0 text-[#001A3B]" />
          <span className="truncate text-sm font-medium text-[#18221F]">{it.filename}</span>
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#A5894E] hover:underline" data-testid="cv-import-view-pdf"><ExternalLink className="h-3 w-3" />Voir le PDF</a>
        </div>
        <button onClick={() => onRemove(it.document_id)} className="rounded p-1 text-[#8695AB] hover:bg-[#F5F7FA]"><X className="h-4 w-4" /></button>
      </div>

      {it.error && <div className="mb-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">Analyse partielle : complétez les champs manuellement.</div>}

      <div className="mb-3 flex flex-wrap gap-1.5">
        {[["plate", "Plaque"], ["infraction_date", "Date"], ["amount", "Montant"], ["notice_number", "N° avis"], ["infraction_type", "Nature"]].map(([k, l]) => <Chip key={k} ok={!!it[k]} label={l} />)}
        <Chip ok={!!it.employee_id} label="Conducteur" />
      </div>

      {it.duplicate && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="cv-import-duplicate">
          <AlertTriangle className="h-4 w-4" /><span className="flex-1">Cette contravention semble déjà avoir été importée ({it.duplicate.reason}).</span>
          <label className="flex items-center gap-1"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="cv-import-force" />Importer quand même</label>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div>
          <Label className="mb-1 block text-[11px] text-[#8695AB]">Catégorie</Label>
          <Select value={it.category || "infraction"} onValueChange={(v) => set("category", v)}>
            <SelectTrigger className="h-9" data-testid="cv-import-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="stationnement">Stationnement (FPS)</SelectItem><SelectItem value="infraction">Infraction</SelectItem></SelectContent>
          </Select>
        </div>
        <F l="Nature de l'infraction" v={it.infraction_type} on={(v) => set("infraction_type", v)} />
        <F l="N° de l'avis" v={it.notice_number} on={(v) => set("notice_number", v)} />
        <F l="Date" type="date" v={it.infraction_date} on={(v) => set("infraction_date", v)} />
        <F l="Heure" type="time" v={it.infraction_time} on={(v) => set("infraction_time", v)} />
        <F l="Montant (€)" type="number" v={it.amount} on={(v) => set("amount", v)} />
        <F l="Échéance paiement" type="date" v={it.payment_deadline} on={(v) => set("payment_deadline", v)} />
        <F l="Points" type="number" v={it.points} on={(v) => set("points", v)} />
        <div className="col-span-2 sm:col-span-3"><F l="Lieu" v={it.location} on={(v) => set("location", v)} /></div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="mb-1 block text-[11px] text-[#8695AB]">Véhicule (plaque {it.plate || "—"})</Label>
          {it.multiple_plates && (
            <div className="mb-1 text-[11px] text-amber-700">Plusieurs plaques détectées — choisissez : {it.plates.join(", ")}</div>
          )}
          <Select value={it.vehicle_id || NONE} onValueChange={(v) => set("vehicle_id", v === NONE ? "" : v)}>
            <SelectTrigger className={`h-9 ${it.vehicle_id ? "" : "text-orange-600"}`} data-testid="cv-import-vehicle"><SelectValue placeholder="Véhicule non trouvé dans la flotte" /></SelectTrigger>
            <SelectContent><SelectItem value={NONE}>— Véhicule non trouvé —</SelectItem>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.registration})</SelectItem>)}</SelectContent>
          </Select>
          {veh && <p className="mt-1 text-xs text-emerald-700"><Car className="mr-1 inline h-3 w-3" />{veh.brand} {veh.model} · {veh.registration}</p>}
        </div>
        <div>
          <Label className="mb-1 block text-[11px] text-[#8695AB]">Conducteur désigné</Label>
          <DriverPicker employees={employees} value={it.employee_id} onChange={(v) => set("employee_id", v)} testid="cv-import-driver" />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={validate} disabled={saving || (it.duplicate && !force)} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="cv-import-validate">
          {saving ? "Enregistrement…" : "Valider et enregistrer"}
        </Button>
      </div>
    </div>
  );
}

const F = ({ l, v, on, type = "text" }) => (
  <div>
    <Label className="mb-1 block text-[11px] text-[#8695AB]">{l}</Label>
    <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="h-9" />
  </div>
);

export default function ContraventionPdfImport({ open, onOpenChange, onDone }) {
  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (open) {
      setItems([]);
      api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    }
  }, [open]);

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (list.length === 0) return;
    setAnalyzing((n) => n + list.length);
    for (const f of list) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/contraventions/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const fields = data.fields || {};
        setItems((prev) => [...prev, {
          document_id: data.document_id, filename: data.filename,
          category: fields.category || "infraction", is_fps: fields.is_fps || false,
          infraction_type: fields.infraction_type || "", notice_number: fields.notice_number || "",
          infraction_date: fields.infraction_date || "", infraction_time: fields.infraction_time || "",
          location: fields.location || "", amount: fields.amount ?? "", points: fields.points ?? "",
          payment_deadline: fields.payment_deadline || "", plate: fields.plate || "",
          plates: data.plates || [], multiple_plates: data.multiple_plates,
          vehicle_id: data.vehicle?.id || "", employee_id: data.suggested_employee_id || "",
          duplicate: data.duplicate, error: data.error,
        }]);
      } catch (e) { toast.error(`${f.name}: ${apiError(e)}`); }
      finally { setAnalyzing((n) => n - 1); }
    }
  }, []);

  const onValidated = (docId) => { setItems((p) => p.filter((i) => i.document_id !== docId)); onDone && onDone(); };
  const onRemove = (docId) => setItems((p) => p.filter((i) => i.document_id !== docId));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UploadCloud className="h-5 w-5" />Importer une contravention PDF</DialogTitle></DialogHeader>

        <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#E2E8F0] p-6 text-center hover:border-[#001A3B]/40" data-testid="cv-pdf-dropzone">
          <UploadCloud className="mb-2 h-8 w-8 text-[#001A3B]" />
          <p className="text-sm font-medium text-[#18221F]">Déposez un ou plusieurs PDF (avis de contravention / FPS)</p>
          <p className="mt-1 text-xs text-[#8695AB]">Analyse automatique par IA (fonctionne aussi sur les scans)</p>
          <input type="file" accept=".pdf" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="cv-pdf-input" />
        </label>

        {analyzing > 0 && <div className="mt-3 flex items-center gap-2 text-sm text-[#001A3B]"><Loader2 className="h-4 w-4 animate-spin" />Analyse de {analyzing} document(s) en cours…</div>}

        <div className="mt-4 space-y-4">
          {items.map((it) => <ImportCard key={it.document_id} item={it} vehicles={vehicles} employees={employees} onValidated={onValidated} onRemove={onRemove} />)}
        </div>

        {items.length === 0 && analyzing === 0 && <p className="py-6 text-center text-sm text-[#8695AB]">Aucun document analysé pour le moment.</p>}
      </DialogContent>
    </Dialog>
  );
}
